import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps, Row } from "@/libraries/wpl/foundations/src";

type tileListContent = {
    headerText: string;
    bodyText: string;
    imageProps: {
        src: string;
        alt: string;
    };
    linkProps: {
        text: string;
        href: string;
        dataEventProps: DataEventProps;
    };
};

export const ResourcesSection = () => {
    const intl = useIntl();

    const tileList: tileListContent[] = [
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.trustCenter.headerText",
                defaultMessage: "Trust Center"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.trustCenter.bodyText",
                defaultMessage:
                    "Keep your organization’s data secure, maintain compliance, and achieve resilience at scale."
            }),
            imageProps: {
                src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/trust-center-cloud-icon-3087608378.webp",

                alt: ""
            },
            linkProps: {
                text: intl.formatMessage({
                    id: "EnterpriseLandingPage.Resources.card.trustCenter.linkText",
                    defaultMessage: "Visit our Trust Center"
                }),
                href: useLocalizedHref("/trust"),
                dataEventProps: {
                    "data-product-key": "enterprise",
                    "data-event-component": "linkButton",
                    "data-event-container": "resourcesSection",
                    "data-event": "clicked",
                    "data-label": intl.formatMessage({
                        id: "EnterpriseLandingPage.Resources.card.trustCenter.dataLabel",
                        defaultMessage: "Visit our Trust Center"
                    }),
                    "data-label-english": "Visit our Trust Center"
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.publicRoadmap.headerText",
                defaultMessage: "Public Roadmap"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.publicRoadmap.bodyText",
                defaultMessage:
                    "Discover the latest features in the Atlassian cloud and see what we’re working on next."
            }),
            imageProps: {
                src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/curved-arrow-icon-3497580723.webp",
                alt: ""
            },
            linkProps: {
                text: intl.formatMessage({
                    id: "EnterpriseLandingPage.Resources.card.publicRoadmap.linkText",
                    defaultMessage: "Check out the Cloud Roadmap"
                }),
                href: useLocalizedHref("/roadmap/cloud"),
                dataEventProps: {
                    "data-product-key": "enterprise",
                    "data-event-component": "linkButton",
                    "data-event-container": "resourcesSection",
                    "data-event": "clicked",
                    "data-label": intl.formatMessage({
                        id: "EnterpriseLandingPage.Resources.card.publicRoadmap.dataLabel",
                        defaultMessage: "Check out the Cloud Roadmap"
                    }),
                    "data-label-english": "Check out the Cloud Roadmap"
                }
            }
        },
        {
            headerText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.community.headerText",
                defaultMessage: "Enterprise Community"
            }),
            bodyText: intl.formatMessage({
                id: "EnterpriseLandingPage.Resources.card.community.bodyText",
                defaultMessage:
                    "Ask questions and learn best practices for our products at scale from Atlassian advocates."
            }),
            imageProps: {
                src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/community-bubble-icon-3561157108.webp",

                alt: ""
            },
            linkProps: {
                text: intl.formatMessage({
                    id: "EnterpriseLandingPage.Resources.card.community.linkText",
                    defaultMessage: "Join the Enterprise Community"
                }),
                href: "https://community.atlassian.com/t5/Enterprise/gh-p/Enterprise",
                dataEventProps: {
                    "data-product-key": "enterprise",
                    "data-event-component": "linkButton",
                    "data-event-container": "resourcesSection",
                    "data-event": "clicked",
                    "data-label": intl.formatMessage({
                        id: "EnterpriseLandingPage.Resources.card.community.dataLabel",
                        defaultMessage: "Join the Enterprise Community"
                    }),
                    "data-label-english": "Join the Enterprise Community"
                }
            }
        }
    ];

    return (
        <UFOSegment name="enterprise-page-resourcesSection">
            <section css={resourcesSectionContainerStyle}>
                <Row layout="12">
                    <h2>
                        {intl.formatMessage({
                            id: "EnterpriseLandingPage.Resources.sectionHeader",
                            defaultMessage: "Teamwork starts with trust"
                        })}
                    </h2>
                </Row>
                <Row layout="4-4-4">
                    {tileList.map((content, index) => {
                        return (
                            <div
                                className="resources-section-container"
                                key={index}
                            >
                                <div className="img-section">
                                    <img
                                        src={content.imageProps.src}
                                        alt={content.imageProps.alt}
                                        css={css({
                                            aspectRatio: "2/1",
                                            objectFit: "contain"
                                        })}
                                        className="enterprise-resources-card-image"
                                    />
                                </div>
                                <div className="text-section">
                                    <h3>{content.headerText}</h3>
                                    <p className="contentBlock-body">
                                        {content.bodyText}
                                    </p>
                                    <div className="contentBlock-linkFlex">
                                        <Link
                                            text={content.linkProps.text}
                                            href={content.linkProps.href}
                                            dataEventProps={
                                                content.linkProps.dataEventProps
                                            }
                                            type={"basic"}
                                            doesArrowAnimate={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </Row>
            </section>
        </UFOSegment>
    );
};

const resourcesSectionContainerStyle = css({
    "padding": "32px 0 0",
    "h2": {
        textAlign: "center",
        fontWeight: "700"
    },
    ".resources-section-container": {
        "padding": "0px",
        "boxSizing": "border-box",
        "display": "flex",
        "flexDirection": "column",
        "margin": "0 0 24px",
        ".img-section": {
            "display": "block",
            "lineHeight": "0",
            ".enterprise-resources-card-image": {
                height: "148px",
                width: "100%"
            }
        },
        ".text-section": {
            "display": "flex",
            "height": "100%",
            "marginBlockStart": "8px",
            "textAlign": "left",
            "flexDirection": "column",
            "h3": {
                marginBlockStart: "32px",
                marginBlockEnd: "0px",
                fontWeight: "700"
            },
            ".contentBlock-body": {
                marginBlockStart: "12px",
                marginBlockEnd: "0px",
                height: "100%"
            },
            ".contentBlock-linkFlex": {
                "marginBlockStart": "24px",
                "marginBlockEnd": "0px",
                "height": "30px",
                "a": {
                    fontSize: "1rem",
                    lineHeight: "1.5rem",
                    fontWeight: 500
                },
                // This is to prevent a weird interaction where the arrow goes to the next line
                "@media (max-width: 1055px) and (min-width: 968px)": {
                    fontSize: "18px"
                },
                [media.below.sm]: {
                    marginBlockStart: "1rem"
                }
            }
        },

        [media.below.sm]: {
            ".text-section": {
                textAlign: "center"
            }
        }
    }
});
